import React, { useState, useEffect, useCallback } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import type { CalendarConnection } from "@/types/task";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Calendar,
  RefreshCw,
  Trash2,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
} from "lucide-react";
import { useConfirm } from "@/context/ConfirmContext";

interface CalendarConnectionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnectionsChanged?: () => void;
}

export function CalendarConnectionsModal({
  open,
  onOpenChange,
  onConnectionsChanged,
}: CalendarConnectionsModalProps) {
  const { confirm } = useConfirm();
  const [connections, setConnections] = useState<CalendarConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await api.get("/api/Calendar/Connections");
      const list = unwrapArray<CalendarConnection>(res);
      setConnections(list);
    } catch (err: any) {
      console.warn("Failed to load calendar connections:", err);
      setErrorMsg(err?.message || "Failed to load calendar connections");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSyncAndRefresh = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      await api.post("/api/Calendar/Sync").catch((e) => {
        console.warn("Sync error:", e);
      });
      await fetchConnections();
      onConnectionsChanged?.();
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to sync calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchConnections();
    }
  }, [open, fetchConnections]);

  const handleStartOAuth = async (provider: "GOOGLE" | "OUTLOOK") => {
    setActionLoading(`start-${provider}`);
    setErrorMsg(null);
    try {
      const res = await api.get(`/api/Calendar/Auth/Start?provider=${provider}`);
      const data = unwrapObject<{ authUrl: string }>(res);
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err: any) {
      setErrorMsg(
        err?.message ||
          `${provider} connection failed. Verify that Client ID is configured in system settings.`
      );
      setActionLoading(null);
    }
  };

  const handleToggleSync = async (conn: CalendarConnection) => {
    const nextState = !conn.sync_enabled;
    setActionLoading(`toggle-${conn.id}`);
    try {
      await api.patch(`/api/Calendar/Connections/${conn.id}`, {
        sync_enabled: nextState,
      });
      setConnections((prev) =>
        prev.map((c) => (c.id === conn.id ? { ...c, sync_enabled: nextState } : c))
      );
      onConnectionsChanged?.();
    } catch (err: any) {
      setErrorMsg(err?.message || "Could not update connection");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (conn: CalendarConnection) => {
    if (!(await confirm({ title: `Disconnect ${conn.provider} account?`, message: conn.provider_account_email, confirmLabel: "Disconnect", destructive: true }))) {
      return;
    }
    setActionLoading(`delete-${conn.id}`);
    try {
      await api.delete(`/api/Calendar/Connections/${conn.id}`);
      setConnections((prev) => prev.filter((c) => c.id !== conn.id));
      onConnectionsChanged?.();
    } catch (err: any) {
      setErrorMsg(err?.message || "Could not disconnect account");
    } finally {
      setActionLoading(null);
    }
  };

  const googleConn = connections.find((c) => c.provider === "GOOGLE");
  const msConn = connections.find((c) => c.provider === "OUTLOOK");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
              <Calendar className="size-5 sm:size-6" />
            </div>
            <div>
              <DialogTitle className="text-lg sm:text-xl font-bold">
                Calendar Integrations & Sync
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Connect Google Calendar or Microsoft Outlook for automatic two-way meeting sync.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {errorMsg && (
          <div className="p-3.5 rounded-lg bg-red-50 text-red-700 text-xs sm:text-sm border border-red-200 flex items-start gap-2.5">
            <AlertTriangle className="size-4 shrink-0 mt-0.5" />
            <div>{errorMsg}</div>
          </div>
        )}

        <div className="space-y-4 py-2">
          {/* Google Calendar Card */}
          <div className="p-4 sm:p-5 rounded-xl border border-border bg-card/60 space-y-3.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white border border-border/80 flex items-center justify-center shadow-xs shrink-0">
                  <svg className="size-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-sm sm:text-base">Google Calendar</div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Auto Google Meet links & two-way calendar sync
                  </p>
                </div>
              </div>

              {googleConn ? (
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`text-xs font-semibold ${
                      googleConn.sync_status === "CONNECTED"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    {googleConn.sync_status === "CONNECTED" ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="size-3.5" /> Connected
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <XCircle className="size-3.5" /> Needs Re-auth
                      </span>
                    )}
                  </Badge>
                </div>
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleStartOAuth("GOOGLE")}
                  disabled={actionLoading === "start-GOOGLE"}
                  className="text-xs"
                >
                  {actionLoading === "start-GOOGLE" ? (
                    <Loader2 className="size-3.5 animate-spin mr-1.5" />
                  ) : null}
                  Connect
                </Button>
              )}
            </div>

            {googleConn && (
              <div className="pt-2 border-t border-border/60 flex items-center justify-between text-xs sm:text-sm">
                <div className="text-muted-foreground">
                  Account:{" "}
                  <span className="font-semibold text-foreground">
                    {googleConn.provider_account_email}
                  </span>
                  {googleConn.last_synced_at && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      (Synced {new Date(googleConn.last_synced_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Sync</span>
                    <Switch
                      checked={googleConn.sync_enabled}
                      onCheckedChange={() => handleToggleSync(googleConn)}
                      disabled={actionLoading === `toggle-${googleConn.id}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDisconnect(googleConn)}
                    disabled={actionLoading === `delete-${googleConn.id}`}
                    className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Disconnect account"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Microsoft Outlook Card */}
          <div className="p-4 sm:p-5 rounded-xl border border-border bg-card/60 space-y-3.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-blue-600 shadow-xs shrink-0">
                  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.5 3v8.5H3V3h8.5zm0 9.5V21H3v-8.5h8.5zm1-9.5H21v8.5h-8.5V3zm0 9.5H21V21h-8.5v-8.5z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-sm sm:text-base">Microsoft Outlook 365</div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Auto Microsoft Teams links & two-way calendar sync
                  </p>
                </div>
              </div>

              {msConn ? (
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`text-xs font-semibold ${
                      msConn.sync_status === "CONNECTED"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    {msConn.sync_status === "CONNECTED" ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="size-3.5" /> Connected
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <XCircle className="size-3.5" /> Needs Re-auth
                      </span>
                    )}
                  </Badge>
                </div>
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleStartOAuth("OUTLOOK")}
                  disabled={actionLoading === "start-OUTLOOK"}
                  className="text-xs"
                >
                  {actionLoading === "start-OUTLOOK" ? (
                    <Loader2 className="size-3.5 animate-spin mr-1.5" />
                  ) : null}
                  Connect
                </Button>
              )}
            </div>

            {msConn && (
              <div className="pt-2 border-t border-border/60 flex items-center justify-between text-xs sm:text-sm">
                <div className="text-muted-foreground">
                  Account:{" "}
                  <span className="font-semibold text-foreground">
                    {msConn.provider_account_email}
                  </span>
                  {msConn.last_synced_at && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      (Synced {new Date(msConn.last_synced_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Sync</span>
                    <Switch
                      checked={msConn.sync_enabled}
                      onCheckedChange={() => handleToggleSync(msConn)}
                      disabled={actionLoading === `toggle-${msConn.id}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDisconnect(msConn)}
                    disabled={actionLoading === `delete-${msConn.id}`}
                    className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Disconnect account"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <RefreshCw className="size-3" />
            <span>Sync runs automatically every 5 minutes in background</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncAndRefresh}
            disabled={loading}
            className="text-xs"
          >
            {loading ? <Loader2 className="size-3.5 animate-spin mr-1" /> : null}
            Refresh & Sync
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
